/*! Copyright (c) Safe As Milk. All rights reserved. */
class QuantityInput extends HTMLElement {
    #boundAdd;
    #boundSelect;
    #boundSubtract;
    #boundValidate;
    #lastValidValue;
    constructor() {
        super();
        this.#boundAdd = this.add.bind(this);
        this.#boundSubtract = this.subtract.bind(this);
        this.#boundValidate = this.validate.bind(this);
        this.#boundSelect = QuantityInput.#selectInputText.bind(this);
    }
    connectedCallback() {
        this.input = this.querySelector('input[type="number"]');
        this.minus = this.querySelector("button[minus]");
        this.plus = this.querySelector("button[plus]");
        this.min = Math.max(1, Number(this.input.getAttribute("min")) || 1);
        this.max = Number(this.input.getAttribute("max") || Infinity);
        this.step = Number(this.input.getAttribute("step") || 1);
        this.#lastValidValue = this.min;
        this.input.value = this.#safeValue(this.input.value);
        this.#lastValidValue = Number(this.input.value);
        setTimeout((() => {
            this.minus.addEventListener("click", this.#boundSubtract);
            this.plus.addEventListener("click", this.#boundAdd);
            this.input.addEventListener("change", this.#boundValidate);
            this.input.addEventListener("blur", this.#boundValidate);
            this.input.addEventListener("select", this.#boundSelect);
            this.input.addEventListener("click", this.#boundSelect);
        }));
    }
    disconnectedCallback() {
        this.minus.removeEventListener("click", this.#boundSubtract);
        this.plus.removeEventListener("click", this.#boundAdd);
        this.input.removeEventListener("change", this.#boundValidate);
        this.input.removeEventListener("blur", this.#boundValidate);
        this.input.removeEventListener("select", this.#boundSelect);
        this.input.removeEventListener("click", this.#boundSelect);
    }
    #safeValue(rawValue) {
        const text = String(rawValue ?? "");
        const number = Number(text);
        if (!/^[0-9]+$/.test(text) || !Number.isFinite(number)) {
            return this.#lastValidValue ?? this.min;
        }
        return Math.max(this.min, Math.min(number, this.max));
    }
    #commit(value) {
        const previousValue = this.#lastValidValue;
        this.input.value = this.#safeValue(value);
        this.#lastValidValue = Number(this.input.value);
        // Normalize unfinished input without sending an unchanged quantity again.
        if (this.#lastValidValue === previousValue) return;
        this.dispatchEvent(new CustomEvent("update", {
            detail: { value: this.input.value }
        }));
    }
    subtract() {
        const value = this.value;
        if (value > this.min) {
            this.#commit(value - Math.min(this.step, value - this.min));
        } else {
            this.input.value = value;
            this.#lastValidValue = value;
        }
    }
    add() {
        const value = this.value;
        if (value < this.max) {
            this.#commit(value + Math.min(this.step, this.max - value));
        } else {
            this.input.value = value;
            this.#lastValidValue = value;
        }
    }
    validate() {
        this.#commit(this.input.value);
    }
    set value(value) {
        this.input.value = this.#safeValue(value);
        this.#lastValidValue = Number(this.input.value);
    }
    get value() {
        // A delayed cart update must not interpret an unfinished edit as deletion.
        return this.#safeValue(this.input.value);
    }
    static #selectInputText(e) {
        e.target.select();
    }
}

customElements.define("quantity-input", QuantityInput);
